import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import LoadingState from '../../../components/ui/LoadingState';
import { scriptLibraryService } from '../../../services/scriptService';
import { useAuth } from '../../../contexts/AuthContext';

const TemplateLibraryModal = ({ isOpen, onClose, onUseTemplate, selectedType }) => {
  const { profile } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [filterGoal, setFilterGoal] = useState('all');

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen, selectedType, profile?.industry]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch templates for user's industry + global templates
      const industryTemplates = await scriptLibraryService?.getByIndustry(
        profile?.industry || 'global'
      );

      // Filter by selected script type
      const filtered = industryTemplates?.filter(
        t => t?.scriptType === selectedType
      );

      setTemplates(filtered);
    } catch (err) {
      setError(err?.message);
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUseTemplate = () => {
    if (selectedTemplate) {
      onUseTemplate(selectedTemplate);
      onClose();
    }
  };

  // Get unique goals for filtering
  const goals = ['all', ...new Set(templates?.map(t => t?.goal))];

  // Filter templates by goal
  const filteredTemplates = filterGoal === 'all' 
    ? templates 
    : templates?.filter(t => t?.goal === filterGoal);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl border border-border max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-foreground">Script Template Library</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Browse proven templates for {selectedType} scripts
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-border bg-muted/30">
          <div className="flex items-center gap-2">
            <Icon name="Filter" size={16} color="var(--color-muted-foreground)" />
            <span className="text-sm font-medium text-foreground">Filter by goal:</span>
            <div className="flex gap-2 flex-wrap">
              {goals?.map(goal => (
                <button
                  key={goal}
                  onClick={() => setFilterGoal(goal)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                    filterGoal === goal
                      ? 'bg-primary text-white' :'bg-card border border-border text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {goal?.split('_')?.map(w => w?.charAt(0)?.toUpperCase() + w?.slice(1))?.join(' ')}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <LoadingState message="Loading templates..." />
          ) : error ? (
            <div className="text-center py-12">
              <Icon name="AlertCircle" size={48} color="var(--color-error)" className="mx-auto mb-4" />
              <p className="text-error">{error}</p>
            </div>
          ) : filteredTemplates?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="FileText" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No Templates Found</h3>
              <p className="text-sm text-muted-foreground">
                No templates available for this combination. Try a different filter.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredTemplates?.map(template => (
                <div
                  key={template?.id}
                  onClick={() => setSelectedTemplate(template)}
                  className={`p-4 rounded-xl border cursor-pointer transition-all ${
                    selectedTemplate?.id === template?.id
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      template?.industry === 'global' ?'bg-accent/10' :'bg-primary/10'
                    }`}>
                      <Icon 
                        name={template?.industry === 'global' ? 'Globe' : 'Target'} 
                        size={20} 
                        color={template?.industry === 'global' ? 'var(--color-accent)' : 'var(--color-primary)'} 
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-foreground mb-1">
                        {template?.title}
                      </h3>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="capitalize">{template?.industry}</span>
                        <span>•</span>
                        <span className="capitalize">{template?.goal?.replace(/_/g, ' ')}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-muted/30 rounded-lg p-3 mb-3">
                    <p className="text-xs text-foreground whitespace-pre-wrap line-clamp-3">
                      {template?.templateText}
                    </p>
                  </div>

                  {template?.coachingNotes && (
                    <div className="flex items-start gap-2 p-2 bg-accent/10 rounded-lg">
                      <Icon name="Lightbulb" size={14} color="var(--color-accent)" className="mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-muted-foreground">{template?.coachingNotes}</p>
                    </div>
                  )}

                  {selectedTemplate?.id === template?.id && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <div className="flex items-center gap-2 text-xs text-success">
                        <Icon name="Check" size={14} />
                        <span className="font-medium">Selected - Click "Use Template" below</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Icon name="Info" size={14} />
            <span>
              {filteredTemplates?.length} template{filteredTemplates?.length !== 1 ? 's' : ''} available
            </span>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              variant="default" 
              iconName="Check"
              iconPosition="left"
              onClick={handleUseTemplate}
              disabled={!selectedTemplate}
            >
              Use Template
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateLibraryModal;